// @file The store for surface onboarding panel displayed by surface_app.vue
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import safeStorage from '@@/bits/safe_storage'
import { SurfaceOnboardingActionTypes, WallCreatedFromType } from '@@/enums'
import type { OnboardingWallViz, WallViz } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import device from 'bits/device'
import { vSet } from 'bits/vue'
import { defineStore } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import { useSurfaceStartingStateStore } from 'pinia/surface_starting_state'
import { useWindowSizeStore } from 'pinia/window_size'
import PadletApi from 'surface/padlet_api'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'
interface SamplePostsStatusMap {
  [stepNumber: number]: SamplePostsStatus
}

export enum SamplePostsStatus {
  None = 'None',
  Loading = 'Loading',
  Generated = 'Generated',
  Errored = 'Errored',
}

const GENERATE_SAMPLE_POSTS_KEY = 'generatedSamplePostIds'

const PANEL_SIZES = {
  DESKTOP: 480,
  SMALLER_THAN_DESKTOP: 400,
}

export const GALLERY_ONBOARDING_MOBILE_HEIGHT = 176

export const useSurfaceOnboardingPanelStore = defineStore('surfaceOnboardingPanel', () => {
  // State
  const hasOnboardingCompleted = ref(false)
  const selectedWallFormat: Ref<WallViz | null> = ref(null)
  const sectionsSelected: Ref<boolean | null> = ref(null)
  const generatingSamplePostsStatusMap = ref<SamplePostsStatusMap>({})
  const randomTitle = computed<string>(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.blankTemplateData?.randomTitle,
  )
  const isActionBarMounted = ref(false)

  const wallViz = computed(() => useSurfaceStore().wallAttributes.viz)
  // Gets the actual attribute of the wall, instead of preview attribute
  const sectionsSaved = computed(() => useSurfaceStore().isSectioned)

  const wallFormats = computed<OnboardingWallViz[]>(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.blankTemplateData?.layouts,
  )

  const savedWallFormat = computed<OnboardingWallViz>(() => {
    const savedFormat = wallFormats.value?.find((format) => format.value === wallViz.value)
    return savedFormat !== undefined ? savedFormat : wallFormats.value?.[0]
  })

  // Getters
  const showOnboardingPanel = computed(
    () => !device.app && isShowingOnboardingPanelInitially.value && !hasOnboardingCompleted.value,
  )
  const isSmallerThanDesktop = computed(() => useWindowSizeStore().isSmallerThanDesktop)

  const isShowingOnboardingPanelInitially = computed<boolean>(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.showOnboardingPanel ?? false,
  )

  const galleryOnboardingFlowData = computed(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.galleryTemplateData,
  )

  const isGalleryTemplatePanel = computed(
    () =>
      useSurfaceStartingStateStore().startingState.onboardingFlow?.wallCreatedFromType === WallCreatedFromType.gallery,
  )

  const xGalleryOnboardingPanelMobile = computed(() => {
    return (
      showOnboardingPanel.value &&
      isGalleryTemplatePanel.value &&
      useWindowSizeStore().isSmallerThanTabletPortrait &&
      isAppUsing('onboardingPanel')
    )
  })

  const xGalleryOnboardingPanelDesktop = computed(() => {
    return (
      showOnboardingPanel.value &&
      isGalleryTemplatePanel.value &&
      !useWindowSizeStore().isSmallerThanTabletPortrait &&
      isAppUsing('onboardingPanel')
    )
  })

  const xOnboardingPanel = computed(() => {
    return showOnboardingPanel.value && isAppUsing('onboardingPanel')
  })

  const xBlankOnboardingPanel = computed(() => {
    return showOnboardingPanel.value && !isGalleryTemplatePanel.value && isAppUsing('onboardingPanel')
  })

  const wallId = computed(() => useSurfaceStore().wallId)
  // call this onMounted
  function setGenerateSamplePostStatus(): void {
    const steps = galleryOnboardingFlowData.value.steps
    steps.forEach((step: any, index: number) => {
      if (step.action === SurfaceOnboardingActionTypes.GenerateSamplePosts && step.samplePostIds !== null) {
        const storageItem = safeStorage.getItem(`${GENERATE_SAMPLE_POSTS_KEY}:${wallId.value as string}:${index}`)
        if (storageItem !== null && JSON.parse(storageItem).length > 0) {
          vSet(generatingSamplePostsStatusMap.value, index, SamplePostsStatus.Generated)
        } else {
          vSet(generatingSamplePostsStatusMap.value, index, SamplePostsStatus.None)
        }
      }
    })
  }

  const panelWidth = computed(() => {
    if (!showOnboardingPanel.value) return 0
    return isSmallerThanDesktop.value ? PANEL_SIZES.SMALLER_THAN_DESKTOP : PANEL_SIZES.DESKTOP
  })

  const currentlySelectedWallFormat = computed(() => {
    if (selectedWallFormat.value !== null) {
      return selectedWallFormat.value
    } else {
      return savedWallFormat.value?.value
    }
  })

  const isSectionsCurrentlySelected = computed(() => {
    if (sectionsSelected.value !== null) {
      return sectionsSelected.value
    } else {
      return sectionsSaved.value
    }
  })

  // Actions
  function setIsActionBarMounted(newValue: boolean): void {
    isActionBarMounted.value = newValue
  }

  function closeOnboardingPanel(): void {
    void fetchWallOnboardingPanelHide()
    hasOnboardingCompleted.value = true
  }

  function dontShowGalleryAgain(templateId: string): void {
    void fetchDontShowGalleryAgain(templateId)
    hasOnboardingCompleted.value = true
  }

  function setSelectedWallFormat(wallFormat: WallViz): void {
    selectedWallFormat.value = wallFormat
  }

  function setSelectedSections(isSectionSelected: boolean): void {
    sectionsSelected.value = isSectionSelected
  }

  async function fetchWallOnboardingPanelHide(): Promise<void> {
    try {
      await PadletApi.WallOnboardingPanel.fetch(window.app?.$store.getters?.wallId)
    } catch (e) {
      captureFetchException(e, { source: ' SurfaceWallOnboardingPanelHide' })
    }
  }

  async function fetchDontShowGalleryAgain(templateId: string): Promise<void> {
    try {
      await PadletApi.WallOnboardingPanel.dontShowGalleryAgain(window.app?.$store.getters?.wallId, templateId)
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceWallOnboardingPanelDontShowGalleryAgain' })
    }
  }

  async function generateSamplePosts(stepNumber: number, samplePostIds: string[]): Promise<void> {
    try {
      vSet(generatingSamplePostsStatusMap.value, stepNumber, SamplePostsStatus.Loading)
      const { data } = await PadletApi.WallOnboardingPanel.generateSamplePosts(wallId.value, samplePostIds)
      const ids: number[] = (data as JsonAPIResource<any>).attributes?.ids
      safeStorage.setItem(
        `${GENERATE_SAMPLE_POSTS_KEY}:${wallId.value as string}:${stepNumber}`, // generatedSamplePostIds:204:0
        asciiSafeStringify(ids),
      )
      vSet(generatingSamplePostsStatusMap.value, stepNumber, SamplePostsStatus.Generated)
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceWallOnboardingPanelGenerateSamplePost' })
    }
  }

  async function deleteSamplePosts(stepNumber: number): Promise<void> {
    try {
      const storageItem = safeStorage.getItem(`${GENERATE_SAMPLE_POSTS_KEY}:${wallId.value as string}:${stepNumber}`)
      if (storageItem !== null) {
        const ids = JSON.parse(storageItem) as number[]
        await Promise.all(ids.map(async (id: number) => await useSurfacePostsStore().deletePost(`c${id}`)))
        safeStorage.removeItem(`${GENERATE_SAMPLE_POSTS_KEY}:${wallId.value as string}:${stepNumber}`)
        vSet(generatingSamplePostsStatusMap.value, stepNumber, SamplePostsStatus.None)
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceWallOnboardingPanelDeleteSamplePost' })
    }
  }

  return {
    // State
    generatingSamplePostsStatusMap,
    sectionsSaved,
    selectedWallFormat,
    sectionsSelected,

    // Getters
    showOnboardingPanel,
    galleryOnboardingFlowData,
    wallFormats,
    savedWallFormat,
    isGalleryTemplatePanel,
    randomTitle,
    isShowingOnboardingPanelInitially,
    panelWidth,
    currentlySelectedWallFormat,
    isSectionsCurrentlySelected,
    isActionBarMounted,

    // Actions,
    setGenerateSamplePostStatus,
    setSelectedWallFormat,
    setSelectedSections,
    xGalleryOnboardingPanelMobile,
    xGalleryOnboardingPanelDesktop,
    xOnboardingPanel,
    xBlankOnboardingPanel,
    closeOnboardingPanel,
    dontShowGalleryAgain,
    generateSamplePosts,
    deleteSamplePosts,
    setIsActionBarMounted,
  }
})
